// Utility to discover available sectors from data files
export const discoverAvailableSectors = async () => {
  try {
    const response = await fetch('/data/sectors.json');
    if (!response.ok) {
      throw new Error(`Failed to load sectors index: ${response.status}`);
    }
    
    const sectorsData = await response.json();
    return sectorsData.sectors || [];
  } catch (error) {
    console.error('Could not load sectors index:', error);
    throw error;
  }
};

export const loadSectorData = async (sectorId) => {
  try {
    const [metadataResponse, organisationsResponse, conferencesResponse] = await Promise.all([
      fetch(`/data/${sectorId}/metadata.json`),
      fetch(`/data/${sectorId}/organisations.json`),
      fetch(`/data/${sectorId}/conferences.json`)
    ]);

    // Check if all files loaded successfully
    if (!metadataResponse.ok || !organisationsResponse.ok || !conferencesResponse.ok) {
      throw new Error(`Failed to load data files for sector: ${sectorId}`);
    }

    const [metadata, organisations, conferences] = await Promise.all([
      metadataResponse.json(),
      organisationsResponse.json(),
      conferencesResponse.json()
    ]);

    // Validate data structure
    if (!Array.isArray(organisations) || !Array.isArray(conferences)) {
      throw new Error(`Invalid data structure for sector: ${sectorId}`);
    }

    return {
      metadata,
      organisations,
      conferences
    };
  } catch (error) {
    console.error(`Error loading sector data for ${sectorId}:`, error);
    throw error;
  }
};

// Load only metadata for faster initial loading
export const loadSectorMetadata = async (sectorId) => {
  try {
    const response = await fetch(`/data/${sectorId}/metadata.json`);
    
    if (!response.ok) {
      throw new Error(`Failed to load metadata for sector: ${sectorId}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error loading sector metadata for ${sectorId}:`, error);
    throw error;
  }
};

// Extract sector ID from conference ID
export const extractSectorFromConferenceId = (conferenceId) => {
  if (!conferenceId) return null;
  
  // Conference IDs are in format: sectorId_org_xxx_conf_yyyy
  const parts = conferenceId.split('_');
  if (parts.length >= 4) {
    // Find the index of 'org' and take everything before it
    const orgIndex = parts.indexOf('org');
    if (orgIndex > 0) {
      return parts.slice(0, orgIndex).join('-');
    }
  }
  
  return null;
};

export const getSampleData = (sectorId = 'sample') => ({
  metadata: {
    sector: sectorId,
    lastUpdated: new Date().toISOString(),
    totalOrganisations: 3,
    totalConferences: 3,
    dataVersion: '1.0.0'
  },
  organisations: [
    {
      id: 'sample_org_001',
      name: 'Sample Professional Association',
      type: 'professional_association',
      description: 'A sample professional organisation for demonstration',
      estimatedMembers: '500+',
      specialisation: ['professional development', 'industry standards'],
      website: 'https://example.com',
      verified: false,
      confidence: 0.7,
      lastUpdated: new Date().toISOString()
    },
    {
      id: 'sample_org_002',
      name: 'Sample Industry Group',
      type: 'industry_body',
      description: 'A sample industry body for demonstration',
      estimatedMembers: '300+',
      specialisation: ['industry advocacy', 'research'],
      website: null,
      verified: false,
      confidence: 0.6,
      lastUpdated: new Date().toISOString()
    },
    {
      id: 'sample_org_003',
      name: 'Sample Government Agency',
      type: 'government',
      description: 'A sample government organisation',
      estimatedMembers: '2000+',
      specialisation: ['policy', 'regulation'],
      website: 'https://government.example.com',
      verified: true,
      confidence: 0.9,
      lastUpdated: new Date().toISOString()
    }
  ],
  conferences: [
    {
      id: 'sample_conf_001',
      organisationId: 'sample_org_001',
      organisationName: 'Sample Professional Association',
      year: 2025,
      name: 'Annual Professional Conference 2025',
      dates: ['2025-09-15', '2025-09-17'],
      location: 'Sample City',
      venue: 'Conference Centre',
      registrationCost: '$450',
      registrationDeadline: '2025-08-15',
      paperSubmissionDeadline: '2025-06-01',
      theme: 'Future of the Industry',
      conferenceUrl: 'https://example.com/conference',
      verified: false,
      confidence: 0.7,
      source: 'sample',
      relevancePercentage: 85,
      relevanceReason: 'Highly relevant to industry professionals',
      lastUpdated: new Date().toISOString()
    },
    {
      id: 'sample_conf_002',
      organisationId: 'sample_org_002',
      organisationName: 'Sample Industry Group',
      year: 2025,
      name: 'Industry Symposium 2025',
      dates: null,
      location: 'Sample City',
      venue: null,
      registrationCost: null,
      registrationDeadline: null,
      paperSubmissionDeadline: null,
      theme: 'Innovation and Growth',
      conferenceUrl: null,
      verified: false,
      confidence: 0.4,
      source: 'sample',
      relevancePercentage: 70,
      relevanceReason: 'Relevant to industry stakeholders',
      lastUpdated: new Date().toISOString()
    }
  ]
});